/**
 * Evidence Lineage Drawer Component
 * Quick-view drawer that can be opened from claims, evidence cards, or audit events
 * Implements the "30-second auditor response" UX pattern
 */
import React, { useState, useEffect } from 'react';
import {
  X,
  Link2,
  FileText,
  ArrowUp,
  ArrowDown,
  ArrowRight,
  Zap,
  User,
  CheckCircle2,
  AlertTriangle,
  ExternalLink,
  RefreshCw,
  Loader2,
  Shield,
  Layers,
  Database,
  Clock,
  Hash
} from 'lucide-react';
import { apiFetch } from '../../services/api_service';

// Mini Flow Node Component
function MiniFlowNode({ type, data, onClick }) {
  const typeStyles = {
    upstream: { icon: ArrowUp, bg: 'bg-blue-500/10', border: 'border-blue-500/30', text: 'text-blue-400' },
    evidence: { icon: FileText, bg: 'bg-teal-500/10', border: 'border-teal-500/50', text: 'text-teal-400' },
    downstream: { icon: ArrowDown, bg: 'bg-purple-500/10', border: 'border-purple-500/30', text: 'text-purple-400' }
  };

  const style = typeStyles[type] || typeStyles.evidence;
  const Icon = style.icon;

  return (
    <div 
      onClick={onClick}
      className={`p-3 rounded-lg ${style.bg} border ${style.border} ${onClick ? 'cursor-pointer hover:opacity-80' : ''} transition-opacity`}
    >
      <div className="flex items-center gap-2">
        <Icon size={14} className={style.text} />
        <span className="text-sm font-medium text-white truncate">{data.title}</span>
        {onClick && <ArrowRight size={12} className={style.text} />}
      </div>
      {data.subtitle && (
        <p className="text-xs text-[#6B6B6B] mt-1 truncate">{data.subtitle}</p>
      )}
    </div>
  );
}

// Impact Badge Component
function ImpactBadge({ count, type, label }) {
  const colors = {
    frameworks: 'bg-teal-500/20 text-teal-400 border-teal-500/30',
    claims: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
    efficiency: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
    warning: 'bg-amber-500/20 text-amber-400 border-amber-500/30'
  };

  return (
    <div className={`px-3 py-2 rounded-lg border ${colors[type]} text-center`}>
      <div className="text-xl font-bold">{count}</div>
      <div className="text-[10px] uppercase">{label}</div>
    </div>
  );
}

// Main Drawer Component
export default function LineageDrawer({ 
  isOpen, 
  onClose, 
  evidenceId, 
  claimId,  // Optional - if opened from a claim, highlight that claim
  sourceContext // 'claim', 'evidence', 'audit' - where drawer was opened from
}) {
  const [loading, setLoading] = useState(false);
  const [lineage, setLineage] = useState(null);
  const [error, setError] = useState(null);

  // Fetch lineage when drawer opens
  useEffect(() => {
    if (isOpen && evidenceId) {
      fetchLineage();
    }
  }, [isOpen, evidenceId]);

  const fetchLineage = async () => {
    if (!evidenceId) return;
    setLoading(true);
    setError(null);
    
    try {
      const data = await apiFetch(`/v1/assurance/evidence-lineage/${evidenceId}`);
      setLineage(data);
    } catch (err) {
      console.error('Error fetching lineage:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const navigateTo = (path) => {
    window.location.href = path;
    onClose();
  };

  if (!isOpen) return null;

  const { lineage_graph, impact_analysis, quality_metrics, file_name } = lineage || {};

  // Determine if this evidence is "orphaned" (no downstream usage)
  const isOrphaned = lineage && 
    (!lineage_graph?.downstream?.claims?.length) && 
    (!lineage_graph?.downstream?.disclosure_cycles?.length);

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/60 z-40 transition-opacity"
        onClick={onClose}
      />

      {/* Drawer */}
      <div className="fixed right-0 top-0 h-full w-full max-w-xl bg-[#0A0A0A] border-l border-[#1F1F1F] z-50 flex flex-col overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-[#1F1F1F] bg-gradient-to-r from-teal-500/10 to-transparent">
          <div className="flex items-center gap-3">
            <Link2 className="h-5 w-5 text-teal-400" />
            <div>
              <h2 className="text-lg font-semibold text-white">Evidence Lineage</h2>
              <p className="text-xs text-[#6B6B6B]">Data DNA • 30-second audit response</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigateTo(`/audit/lineage?evidence=${evidenceId}`)}
              className="p-2 rounded-lg hover:bg-[#1F1F1F] text-[#6B6B6B] hover:text-white transition-colors"
              title="Open Full Page"
            >
              <ExternalLink size={16} />
            </button>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-[#1F1F1F] text-[#6B6B6B] hover:text-white transition-colors"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-teal-400" />
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <AlertTriangle className="h-10 w-10 text-red-400 mx-auto mb-3" />
              <p className="text-sm text-white mb-2">Failed to load lineage</p>
              <p className="text-xs text-[#6B6B6B] mb-4">{error}</p>
              <button
                onClick={fetchLineage}
                className="px-4 py-2 rounded-lg bg-teal-500/20 text-teal-400 text-sm hover:bg-teal-500/30"
              >
                <RefreshCw size={14} className="inline mr-2" />
                Retry
              </button>
            </div>
          ) : lineage ? (
            <>
              {/* Evidence Header */}
              <div className="p-4 rounded-lg bg-[#111111] border border-[#1F1F1F]">
                <div className="flex items-start gap-3">
                  <div className="h-12 w-12 rounded-lg bg-teal-500/20 flex items-center justify-center shrink-0">
                    <FileText className="h-6 w-6 text-teal-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-base font-semibold text-white truncate">
                      {file_name || 'Unnamed Evidence'}
                    </h3>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      <span className="px-2 py-0.5 rounded bg-[#1F1F1F] text-[#6B6B6B] text-xs font-mono">
                        {evidenceId?.substring(0, 12)}...
                      </span>
                      {quality_metrics?.approval_status === 'approved' && (
                        <span className="px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-400 text-xs">
                          ✓ Approved
                        </span>
                      )}
                      {isOrphaned && (
                        <span className="px-2 py-0.5 rounded bg-amber-500/20 text-amber-400 text-xs flex items-center gap-1">
                          <AlertTriangle size={10} />
                          Not in use
                        </span>
                      )}
                    </div>
                    {lineage.uploaded_at && (
                      <p className="text-xs text-[#6B6B6B] mt-2 flex items-center gap-1">
                        <Clock size={10} />
                        Uploaded {new Date(lineage.uploaded_at).toLocaleString()}
                        {lineage.uploaded_by_name && ` by ${lineage.uploaded_by_name}`}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Impact Summary - Key Stats */}
              <div className="grid grid-cols-4 gap-2">
                <ImpactBadge 
                  count={impact_analysis?.framework_count || 0} 
                  type="frameworks" 
                  label="Frameworks" 
                />
                <ImpactBadge 
                  count={impact_analysis?.requirements_satisfied || 0} 
                  type="claims" 
                  label="Requirements" 
                />
                <ImpactBadge 
                  count={impact_analysis?.claims_approved || 0} 
                  type="claims" 
                  label="Approved" 
                />
                <ImpactBadge 
                  count={`${impact_analysis?.upload_efficiency || 1}x`} 
                  type="efficiency" 
                  label="Efficiency" 
                />
              </div>

              {/* Efficiency Message */}
              {impact_analysis?.upload_efficiency > 1 && (
                <div className="p-3 rounded-lg bg-teal-500/10 border border-teal-500/30">
                  <p className="text-xs text-teal-400">
                    💡 <strong>Cross-Framework Intelligence:</strong> {impact_analysis.efficiency_message}
                  </p>
                </div>
              )}

              {/* Orphaned Warning */}
              {isOrphaned && (
                <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/30">
                  <p className="text-xs text-amber-400 flex items-start gap-2">
                    <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                    <span>
                      <strong>Orphaned Evidence:</strong> This file is not currently used in any disclosure claims. 
                      Consider mapping it to relevant framework requirements or archiving if no longer needed.
                    </span>
                  </p>
                </div>
              )}

              {/* Mini Flow Diagram */}
              <div className="space-y-3">
                <h4 className="text-xs font-semibold text-[#6B6B6B] uppercase">Data Flow</h4>

                {/* Upstream */}
                <div>
                  <p className="text-[10px] text-[#6B6B6B] uppercase mb-2 flex items-center gap-1">
                    <ArrowUp size={10} /> Upstream (Source)
                  </p>
                  {lineage_graph?.upstream?.obligation ? (
                    <MiniFlowNode
                      type="upstream"
                      data={{
                        title: lineage_graph.upstream.obligation.obligation_code,
                        subtitle: lineage_graph.upstream.obligation.obligation_title
                      }}
                      onClick={() => navigateTo(`/obligations/action-queue?selected=${lineage_graph.upstream.obligation.obligation_id}`)}
                    />
                  ) : (
                    <div className="p-3 rounded-lg bg-[#111111] border border-dashed border-[#2F2F2F] text-xs text-[#6B6B6B] text-center">
                      Direct upload (no obligation linked)
                    </div>
                  )}
                </div>

                {/* Current Evidence */}
                <div className="flex justify-center">
                  <ArrowDown size={16} className="text-[#2F2F2F]" />
                </div>
                <MiniFlowNode
                  type="evidence"
                  data={{
                    title: file_name || 'Evidence File',
                    subtitle: 'Current evidence (you are here)'
                  }}
                />
                <div className="flex justify-center">
                  <ArrowDown size={16} className="text-[#2F2F2F]" />
                </div>

                {/* Downstream Claims */}
                <div>
                  <p className="text-[10px] text-[#6B6B6B] uppercase mb-2 flex items-center gap-1">
                    <ArrowDown size={10} /> Downstream (Usage) 
                    <span className="ml-auto px-1.5 py-0.5 rounded bg-purple-500/20 text-purple-400">
                      {lineage_graph?.downstream?.claims?.length || 0}
                    </span>
                  </p>
                  
                  {!lineage_graph?.downstream?.claims?.length ? (
                    <div className="p-3 rounded-lg bg-[#111111] border border-dashed border-[#2F2F2F] text-xs text-[#6B6B6B] text-center">
                      Not yet used in any claims
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {lineage_graph.downstream.claims.slice(0, 5).map((claim, idx) => (
                        <div
                          key={idx}
                          onClick={() => navigateTo(`/disclosures?claim=${claim.claim_id}`)}
                          className={`p-2 rounded-lg bg-purple-500/10 border border-purple-500/30 cursor-pointer hover:border-purple-500/50 transition-colors ${
                            claimId === claim.claim_id ? 'ring-2 ring-purple-500' : ''
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            <span className="px-1.5 py-0.5 rounded bg-[#1F1F1F] text-white text-[10px] font-medium">
                              {claim.framework_code}
                            </span>
                            <span className="text-xs text-white truncate flex-1">
                              {claim.requirement_code}: {claim.claim_value || 'No value'}
                            </span>
                            {claim.claim_status === 'approved' && (
                              <CheckCircle2 size={12} className="text-emerald-400 shrink-0" />
                            )}
                          </div>
                        </div>
                      ))}
                      {lineage_graph.downstream.claims.length > 5 && (
                        <p className="text-xs text-[#6B6B6B] text-center">
                          +{lineage_graph.downstream.claims.length - 5} more claims
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Quality Metrics Mini */}
              {quality_metrics && (
                <div className="p-3 rounded-lg bg-[#111111] border border-[#1F1F1F]">
                  <h4 className="text-xs font-semibold text-[#6B6B6B] uppercase mb-2">Quality Metrics</h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <p className="text-[10px] text-[#6B6B6B]">Data Quality</p>
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-1.5 bg-[#1F1F1F] rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-emerald-500 rounded-full"
                            style={{ width: `${quality_metrics.data_quality_score || 0}%` }}
                          />
                        </div>
                        <span className="text-xs text-white font-medium">
                          {quality_metrics.data_quality_score || 0}%
                        </span>
                      </div>
                    </div>
                    <div>
                      <p className="text-[10px] text-[#6B6B6B]">Audit Trail</p>
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-1.5 bg-[#1F1F1F] rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-blue-500 rounded-full"
                            style={{ width: `${quality_metrics.audit_trail_completeness || 0}%` }}
                          />
                        </div>
                        <span className="text-xs text-white font-medium">
                          {quality_metrics.audit_trail_completeness || 0}%
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="space-y-2">
                <button
                  onClick={() => navigateTo(`/audit/trail?aggregateType=Evidence&aggregateId=${evidenceId}`)}
                  className="w-full p-3 rounded-lg bg-purple-500/10 border border-purple-500/30 hover:bg-purple-500/20 transition-colors flex items-center justify-between"
                >
                  <div className="flex items-center gap-2">
                    <Shield size={16} className="text-purple-400" />
                    <span className="text-sm text-white">View Complete Audit Trail</span>
                  </div>
                  <ArrowRight size={14} className="text-purple-400" />
                </button>

                <button
                  onClick={() => navigateTo(`/audit/lineage?evidence=${evidenceId}`)}
                  className="w-full p-3 rounded-lg bg-teal-500/10 border border-teal-500/30 hover:bg-teal-500/20 transition-colors flex items-center justify-between"
                >
                  <div className="flex items-center gap-2">
                    <ExternalLink size={16} className="text-teal-400" />
                    <span className="text-sm text-white">Open Full Lineage Page</span>
                  </div>
                  <ArrowRight size={14} className="text-teal-400" />
                </button>
              </div>
            </>
          ) : (
            <div className="text-center py-12">
              <Link2 size={32} className="text-[#2F2F2F] mx-auto mb-3" />
              <p className="text-sm text-[#6B6B6B]">No evidence selected</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-3 border-t border-[#1F1F1F] bg-[#0A0A0A]">
          <p className="text-[10px] text-[#6B6B6B] text-center">
            Superpower #1: Cross-Framework Intelligence • Superpower #3: Audit Defensibility
          </p>
        </div>
      </div>
    </>
  );
}

// Hook to manage drawer state
export function useLineageDrawer() {
  const [isOpen, setIsOpen] = useState(false);
  const [evidenceId, setEvidenceId] = useState(null);
  const [claimId, setClaimId] = useState(null);
  const [sourceContext, setSourceContext] = useState(null);

  const openDrawer = (options) => {
    setEvidenceId(options.evidenceId);
    setClaimId(options.claimId || null);
    setSourceContext(options.sourceContext || 'evidence');
    setIsOpen(true);
  };

  const closeDrawer = () => {
    setIsOpen(false);
    setEvidenceId(null);
    setClaimId(null);
    setSourceContext(null);
  };

  return {
    isOpen,
    evidenceId,
    claimId,
    sourceContext,
    openDrawer,
    closeDrawer
  };
}
